import { getModule } from '@erp-boilerplate/shared';
import type { ModuleKey } from '@erp-boilerplate/types';

/**
 * Route paths.
 *
 * Module base paths are not repeated here — they live in the shared registry and
 * are read through `modulePath`. What this file owns is the paths that have no
 * module: authentication, the account screens, and error pages.
 */

export const ROUTES = {
  root: '/',
  login: '/login',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  dashboard: '/dashboard',
  profile: '/settings/profile',
  changePassword: '/settings/password',
  forbidden: '/403',
} as const;

/**
 * Customer portal paths (Phase 6).
 *
 * A separate namespace from `ROUTES`, and separate on purpose: nothing in the
 * portal is a module, so none of these come from the module registry, and no
 * portal screen should ever be reachable by a path built with `modulePath`.
 */
export const PORTAL_ROUTES = {
  root: '/portal',
  login: '/portal/login',
  dashboard: '/portal',
  profile: '/portal/profile',
  quotations: '/portal/quotations',
  quotation: (id: string) => `/portal/quotations/${id}`,
  quotationPrint: (id: string) => `/portal/quotations/${id}/print`,
  sales: '/portal/purchases',
  sale: (id: string) => `/portal/purchases/${id}`,
  invoice: (id: string) => `/portal/purchases/${id}/invoice`,
  payments: '/portal/payments',
  receipt: (id: string) => `/portal/payments/${id}/receipt`,
  rentals: '/portal/rentals',
  rental: (id: string) => `/portal/rentals/${id}`,
  buybacks: '/portal/buybacks',
  buyback: (id: string) => `/portal/buybacks/${id}`,
  notifications: '/portal/notifications',
  preferences: '/portal/preferences',
} as const;

/** The base path of a module, e.g. `modulePath('users')` → `/users`. */
export const modulePath = (key: ModuleKey): string => getModule(key)?.path ?? '/';

/** Record routes, so a link to a detail screen is never hand-assembled. */
export const recordPath = {
  list: (key: ModuleKey): string => modulePath(key),
  new: (key: ModuleKey): string => `${modulePath(key)}/new`,
  detail: (key: ModuleKey, id: string): string => `${modulePath(key)}/${id}`,
  edit: (key: ModuleKey, id: string): string => `${modulePath(key)}/${id}/edit`,
};

/** The nested patterns a module's own route file registers under its base path. */
export const MODULE_CHILD_PATHS = {
  list: '',
  new: 'new',
  detail: ':id',
  edit: ':id/edit',
} as const;
